'use client';

import {useAuthorization} from '@/matic/hooks/useAuthorization';
import {useNavigate, useLocation, Outlet} from 'react-router-dom';
import {useEffect} from 'react';
import LoadingSpinner from '@/matic/components/ui/LoadingSpinner';
import {useAppContext} from '@/matic/providers/AppProvider';

/**
 * Determines the required admin permission for a given pathname.
 *
 * Maps admin sub-sections to granular permissions so that
 * quality users can only access master-data, not users or document-config.
 */
function getRequiredPermission(pathname: string) {
  if (pathname.startsWith('/admin/master-data'))
    return 'admin.access_master_data';
  if (pathname.startsWith('/admin/document-config'))
    return 'admin.access_document_config';
  // Users + Groups are tabs on a single page; require the lower bar
  // (``admin.access_groups`` = admin + quality) so Quality can land
  // on the Groups tab. The page itself hides the Users tab when the
  // viewer lacks ``admin.access_users``.
  if (pathname.startsWith('/admin/users')) return 'admin.access_groups';
  if (pathname.startsWith('/admin/groups')) return 'admin.access_groups';
  return 'admin.access_section';
}

/**
 * Admin Layout - Role-based guard for admin routes
 *
 * This layout component acts as a role-based guard for all admin routes.
 * It checks section-specific permissions based on the current path:
 * - /admin/master-data/* → admin.access_master_data (admin + quality)
 * - /admin/users/* → admin.access_users (admin only)
 * - /admin/document-config/* → admin.access_document_config (admin only)
 *
 * Layer 1 (Authentication): Handled by parent ProtectedLayout
 * Layer 2 (Role-Based Authorization): This component checks section-specific permissions
 */
export function AdminLayout() {
  const {user} = useAppContext();
  const {can} = useAuthorization();
  const navigate = useNavigate();
  const location = useLocation();

  const requiredPermission = getRequiredPermission(location.pathname);
  const hasAccess = can(requiredPermission);

  useEffect(() => {
    if (user && !hasAccess) {
      navigate('/unauthorized');
    }
  }, [user, hasAccess, navigate]);

  // Only show the loading M on a true cold boot (no user yet). `user` is the
  // *stable* user from AppContext, so it stays defined during silent OIDC
  // token refreshes — preventing M flashes on tab switches into /admin/*.
  if (!user) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // UX-only authorization check - backend enforces actual authorization
  if (!hasAccess) {
    return null; // Redirect will happen via useEffect
  }

  return <Outlet />;
}
